import { Link } from "react-router-dom";
import { ArrowRight, FileSearch, Target, Rocket, BarChart3, TrendingUp } from "lucide-react";
import { ProcessTrack } from "@/components/luminous/interior";

interface ServicesMethodologyProps {
  language: 'es' | 'en';
}

/**
 * Metodología del hub de servicios en la dirección luminosa: recorrido
 * conectado, sin pinning ni altura artificial, con el plazo de cada etapa
 * visible como entregable temporal. Contenido sin cambios.
 */
export const ServicesMethodology = ({ language }: ServicesMethodologyProps) => {
  const content = {
    es: {
      overline: "Proceso",
      title: "Cómo trabajo",
      subtitle: "Un proceso estructurado de 5 pasos, con un entregable y un plazo estimado en cada etapa.",
      steps: [
        {
          icon: FileSearch,
          title: "Diagnóstico",
          description: "Análisis técnico de tu situación actual, competencia y oportunidades de mercado.",
          duration: "1-2 días",
        },
        {
          icon: Target,
          title: "Estrategia",
          description: "Plan de acción priorizado según impacto y recursos disponibles.",
          duration: "3-5 días",
        },
        {
          icon: Rocket,
          title: "Implementación",
          description: "Ejecución técnica y de contenido con seguimiento continuo.",
          duration: "30-90 días",
        },
        {
          icon: BarChart3,
          title: "Medición",
          description: "Tracking de KPIs, reportes mensuales y análisis de resultados.",
          duration: "Mensual",
        },
        {
          icon: TrendingUp,
          title: "Escalamiento",
          description: "Optimización continua y expansión basada en datos reales.",
          duration: "Continuo",
        }
      ],
      cta: "Comenzar con reunión inicial gratuita",
      guarantee: "Sin compromiso · Resultados medibles"
    },
    en: {
      overline: "Process",
      title: "How I work",
      subtitle: "A structured 5-step process, with a deliverable and an estimated timeframe at each stage.",
      steps: [
        {
          icon: FileSearch,
          title: "Diagnosis",
          description: "Technical analysis of your current situation, competition, and market opportunities.",
          duration: "1-2 days",
        },
        {
          icon: Target,
          title: "Strategy",
          description: "Prioritized action plan based on impact and available resources.",
          duration: "3-5 days",
        },
        {
          icon: Rocket,
          title: "Implementation",
          description: "Technical and content execution with continuous monitoring.",
          duration: "30-90 days",
        },
        {
          icon: BarChart3,
          title: "Measurement",
          description: "KPI tracking, monthly reports, and results analysis.",
          duration: "Monthly",
        },
        {
          icon: TrendingUp,
          title: "Scaling",
          description: "Continuous optimization and data-driven expansion.",
          duration: "Ongoing",
        }
      ],
      cta: "Start with free initial consultation",
      guarantee: "No commitment · Measurable results"
    }
  };

  const t = content[language];

  return (
    <section id="metodologia" className="v3-section-tight scroll-mt-24 bg-[color:var(--v3-mineral)]">
      <div className="v3-shell">
        <header className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-14">
          <div className="lg:col-span-7">
            <p className="v3-micro">{t.overline}</p>
            <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.5rem, 2.3vw, 2.05rem)" }}>
              {t.title}
            </h2>
          </div>
          <p className="v3-body lg:col-span-5">{t.subtitle}</p>
        </header>

        <div className="mt-10">
          <ProcessTrack
            steps={t.steps.map((s) => ({
              title: s.title,
              detail: s.description,
              output: s.duration,
            }))}
          />
        </div>

        <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3">
          <Link to={language === 'es' ? '/diagnostico-seo-gratis' : '/en/free-seo-diagnosis'} className="v3-btn">
            {t.cta}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <p className="v3-note">{t.guarantee}</p>
        </div>
      </div>
    </section>
  );
};
